import React from "react";
import moment from "moment";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsSelect from "../clients/Tools/kit/ToolsSelect";
import ToolsClientSearch from "../clients/Tools/kit/ToolsClientSearch";
import PlaybookBuildService from "../../services/PlaybookBuildService";
import PlaybookRunService from "../../services/PlaybookRunService";
import PlaybookInputFieldService from "../../services/PlaybookInputFieldService";
import UtilityService from "../../services/UtilityService";
import AgentRunOutcome from "./AgentRunOutcome";
import AgentFindingCounts from "./AgentFindingCounts";
import AgentSourceList from "./AgentSourceList";
import AgentRunRequestField from "./AgentRunRequestField";
import PlaybookDraftLineCoverage from "./PlaybookDraftLineCoverage";
import PlaybookInputFieldsForm from "./PlaybookInputFieldsForm";

import "./build-page.css";
import "./playbook-build-page.css";

class PlaybookBuildDraftCard extends WComponent {
	constructor(props) {
		super(props);
		const periods = PlaybookRunService.recentCompletedMonths();
		const testFields = PlaybookInputFieldService.fieldsOf({ version: props.session.draft });
		this.state = {
			periods,
			testPeriodEnd: periods[0].value,
			testRequest: "",
			testFieldValues: PlaybookInputFieldService.initialValues({ fields: testFields }),
			testClient: null,
			testSources: null,
			testCanRun: false,
			isStartingTest: false,
		};
	}

	componentDidUpdate(previousProps) {
		const previousDraft = previousProps.session.draft;
		const { draft } = this.props.session;
		const isNewDraftVersion = !!draft && (!previousDraft || previousDraft.versionId !== draft.versionId);
		if (isNewDraftVersion) {
			const testFields = PlaybookInputFieldService.fieldsOf({ version: draft });
			this.update({ testFieldValues: PlaybookInputFieldService.initialValues({ fields: testFields }) });
		}
		if (isNewDraftVersion && this.state.testClient) this.onChooseTestClient({ client: this.state.testClient });
	}

	onTestFieldChanged = ({ key, value }) => {
		this.setState(previousState => ({ testFieldValues: { ...previousState.testFieldValues, [key]: value } }));
	};

	onChooseTestClient = async ({ client }) => {
		const { session, onError } = this.props;
		await this.update({ testClient: client, testSources: null, testCanRun: false });
		try {
			const { sources, canRun } = await PlaybookRunService.checkRunSources({
				playbookId: session.draft.playbookId,
				clientId: client.id,
				draftVersionId: session.draft.versionId,
			});
			await this.update({ testSources: sources, testCanRun: canRun });
		} catch (error) {
			onError({ message: UtilityService.describeRequestError({ error, fallback: "The test client's sources could not be checked." }) });
		}
	};

	onClearTestClient = () => {
		this.update({ testClient: null, testSources: null, testCanRun: false });
	};

	onTestRequestChanged = testRequest => {
		this.update({ testRequest });
	};

	onTestPeriodChanged = testPeriodEnd => {
		this.update({ testPeriodEnd });
	};

	onStartTest = async () => {
		const { session, onStartTest } = this.props;
		const { testClient, testPeriodEnd, testRequest, testFieldValues } = this.state;
		const { takesRequest, needsPeriod } = PlaybookRunService.runInputsNeededBy({ agent: session.draft });
		const testFields = PlaybookInputFieldService.fieldsOf({ version: session.draft });
		const hasTestFields = testFields.length > 0;
		const fieldValues = hasTestFields ? PlaybookInputFieldService.valuesForRun({ fields: testFields, values: testFieldValues }) : null;
		const inputs = PlaybookRunService.inputsForRun({ needsPeriod, periodEnd: testPeriodEnd, takesRequest, request: testRequest, fieldValues });
		await this.update({ isStartingTest: true });
		await onStartTest({ clientId: testClient.id, inputs });
		await this.update({ isStartingTest: false });
	};

	renderTestRun = run => {
		return (
			<a
				key={run.id}
				className={clsx("playbook-build__test-run", !run.isOfCurrentDraft && "playbook-build__test-run--old")}
				href={`/playbooks/runs/${run.id}`}
				target="_blank"
				rel="noopener noreferrer"
			>
				<span className="playbook-build__test-run-main">
					<span className="playbook-build__test-run-client">{run.client.name}</span>
					<span className="tk-muted">
						{PlaybookRunService.periodLabelFor({ periodEnd: run.period_end })} · {moment(run.created_at).fromNow()}
						{!run.isOfCurrentDraft && " · an earlier draft"}
					</span>
				</span>
				<AgentRunOutcome run={run} showReason={false} />
				<AgentFindingCounts run={run} />
				<Icon.ExternalLink size={13} className="tk-muted" />
			</a>
		);
	};

	renderDraftJudgments({ draft }) {
		const { judgments, note } = PlaybookBuildService.describeDecisionPoints({ decisionPoints: draft.decisionPoints, tracking: draft.tracking || null });
		return (
			<div className="playbook-build__judgments">
				<span className="tk-text-strong">Decisions it records on every run</span>
				{judgments.length > 0 && (
					<ul className="playbook-build__judgment-list">
						{judgments.map(judgment => (
							<li key={judgment.key} className="playbook-build__judgment">
								<span>
									{judgment.cannotCollect && <span className="playbook-build__judgment-gap" title="It cannot collect everything this decision depends on yet" />}
									{judgment.question}
								</span>
								{judgment.factsText && <span className="playbook-build__judgment-facts">{judgment.factsText}</span>}
								{judgment.coverageText && <span className="playbook-build__judgment-facts">{judgment.coverageText}</span>}
							</li>
						))}
					</ul>
				)}
				{note && <span className="tk-muted">{note}</span>}
			</div>
		);
	}

	renderDraftTest({ draft }) {
		const { periods, testPeriodEnd, testClient, isStartingTest, testSources, testCanRun, testRequest, testFieldValues } = this.state;
		const { takesRequest, needsPeriod } = PlaybookRunService.runInputsNeededBy({ agent: draft });
		const isTestRequestReady = PlaybookRunService.isRunRequestReady({ takesRequest, request: testRequest });
		const testFields = PlaybookInputFieldService.fieldsOf({ version: draft });
		const hasTestFields = testFields.length > 0;
		const missingTestFields = PlaybookInputFieldService.missingRequired({ fields: testFields, values: testFieldValues });
		const areTestFieldsReady = missingTestFields.length === 0;
		const requestReason = isTestRequestReady ? "" : "Say what you want first";
		const fieldsReason = areTestFieldsReady ? "" : "Fill in the required fields first";
		const blockedReason = requestReason || fieldsReason;

		return (
			<div className="playbook-build__test">
				<span className="tk-text-strong">Test it on a client</span>
				<ToolsClientSearch chosen={testClient} onChoose={this.onChooseTestClient} onClear={this.onClearTestClient} placeholder="Search a QuickBooks client…" />
				{testClient && testSources && <AgentSourceList sources={testSources} />}
				{testClient && testSources && !testCanRun && (
					<span className="playbook-build__source-note">
						This client is missing a required source. Connect it on the client's page, mark it Helpful in Sources and build again, or test on another client.
					</span>
				)}
				{takesRequest && (
					<AgentRunRequestField value={testRequest} example={draft.requestExample} onChange={this.onTestRequestChanged} isCompact disabled={isStartingTest} />
				)}
				{needsPeriod && <ToolsSelect value={testPeriodEnd} options={periods} onChange={this.onTestPeriodChanged} />}
				{hasTestFields && (
					<PlaybookInputFieldsForm fields={testFields} values={testFieldValues} onChange={this.onTestFieldChanged} disabled={isStartingTest} />
				)}
				{hasTestFields && <span className="tk-muted">A run with a form is for one client at a time, since its values belong to that client.</span>}
				<button
					type="button"
					className="tk-button tk-button--secondary tk-button--compact"
					onClick={this.onStartTest}
					disabled={!testClient || !testCanRun || !isTestRequestReady || !areTestFieldsReady || isStartingTest}
					title={blockedReason}
				>
					<Icon.Play size={13} />
					{isStartingTest ? "Starting…" : "Run test"}
				</button>
				<span className="tk-muted">Tests stay private: they never show in Results or on the client's page.</span>
			</div>
		);
	}

	renderPublish({ draft }) {
		const { session, isPublishing, onPublish } = this.props;
		return (
			<div className="playbook-build__publish">
				<button type="button" className="tk-button tk-button--primary" onClick={onPublish} disabled={!session.canPublish || isPublishing}>
					<Icon.UploadCloud size={14} />
					{isPublishing ? "Publishing…" : `Publish version ${draft.versionNumber}`}
				</button>
				{session.whyNotPublish && <span className="tk-muted">{session.whyNotPublish}</span>}
			</div>
		);
	}

	render() {
		const { session, isSending, isBuilding, onBuild } = this.props;
		const isOpen = session.status === "open";
		const { draft } = session;
		const whyOff = PlaybookBuildService.whyBuildIsOff({ session });
		const isPublished = session.status === "published";
		const isHeadShown = !!draft || isPublished;
		const stages = PlaybookBuildService.describeBuildStages({ session });
		const buildStage = stages.find(stage => stage.key === "build");
		const isBuildAgainOptional = buildStage.state === "done" && session.isReadyToBuild;
		const buildAgainLabel = isBuildAgainOptional ? "You can build again with your latest answers" : "Build again";
		const isNewPlaybook = !session.playbookId;
		const firstDraftNumber = isNewPlaybook ? 1 : null;
		const nextDraftNumber = draft ? draft.versionNumber + 1 : firstDraftNumber;
		const buildingLabel = nextDraftNumber ? `Building draft ${nextDraftNumber}…` : "Building the draft…";
		const idleBuildLabel = draft ? buildAgainLabel : "Build the draft";
		const buildLabel = isBuilding ? buildingLabel : idleBuildLabel;
		const isBuildOff = !session.isReadyToBuild || isBuilding || isSending;
		const isBriefUnanswered = !!whyOff && whyOff.reason === "briefUnanswered";
		const isNoDraftYetShown = isOpen && !draft && !session.isReadyToBuild && isBriefUnanswered;
		const noDraftYetText = "No draft yet. Once the guide has what it needs, Build the draft turns on here and the draft appears in this tab.";
		const whyOffText = isNoDraftYetShown ? noDraftYetText : whyOff && whyOff.text;

		return (
			<section className="build-card build-card--draft tk-card">
				{isHeadShown && (
					<div className="build-card__head">
						{draft && <span className="tk-badge tk-badge--accent">Draft {draft.versionNumber}</span>}
						{isPublished && <span className="tk-badge tk-badge--success">Published</span>}
					</div>
				)}
				{!draft && session.isReadyToBuild && (
					<div className="tk-muted">The brief is complete. Build the draft now; you can keep refining with the guide and build again.</div>
				)}
				{whyOff && <div className="tk-muted">{whyOffText}</div>}
				{draft && (
					<div className="playbook-build__draft-summary">
						<strong>{draft.name}</strong>
						<span>{draft.description}</span>
						<span className="tk-muted">
							{draft.category} · built {moment(draft.builtAt).fromNow()} — {draft.changeSummary}
						</span>
						{this.renderDraftJudgments({ draft })}
						<PlaybookDraftLineCoverage
							lineCoverage={draft.lineCoverage}
							isOpen={isOpen}
							isSaving={this.props.isSavingLines}
							onWaive={this.props.onWaiveLine}
							onClearWaiver={this.props.onClearLineWaiver}
						/>
					</div>
				)}
				{isOpen && (
					<button
						type="button"
						className={clsx("tk-button", "tk-button--compact", draft ? "tk-button--secondary" : "tk-button--primary")}
						onClick={onBuild}
						disabled={isBuildOff}
					>
						<Icon.Box size={13} />
						{buildLabel}
					</button>
				)}
				{draft && isOpen && this.renderDraftTest({ draft })}
				{session.testRuns.length > 0 && <div className="playbook-build__test-runs">{session.testRuns.map(this.renderTestRun)}</div>}
				{isOpen && draft && this.renderPublish({ draft })}
			</section>
		);
	}
}

export default PlaybookBuildDraftCard;
